import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogout } from '@/features/Auth/hooks/useLogout';
import { useAppSelector } from '../../app/hooks';
import { getPlatformInfo, getDeviceId } from '@/shared/utils/platform';
import { toast } from 'react-toastify';
import { useUser } from '@/features/Auth/hooks/useUser';
import NotificationPanel from './NotificationPanel';
import { getNotificationEventsApi, getNotificationUnreadCountApi } from '@/shared/api/sharedService';
import { NotificationEventItem } from '@/shared/types/commonTypes';
import QRCodeModal from '@/features/HealthRecords/components/QRCodeModal';
import LinkNewPatientForm from '@/features/Dashboard/components/LinkNewPatientForm';
import { useNavigationBlock } from '@/shared/contexts/NavigationBlockContext';

interface HeaderProps {
  title?: string | React.ReactNode;
  className?: string;
  toggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  title = "",
  className = '',
  toggleSidebar = () => {}
}) => {
  const auth = useAppSelector((state) => state.auth);
  const { fullName, practiceInfo, authUserId } = useUser();
  const logoutMutation = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAndNavigate } = useNavigationBlock();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<NotificationEventItem[]>([]);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const notificationRef = useRef<HTMLDivElement>(null);
  
  // QR Code state
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  
  // Link New Patient state
  const [isPatientFormOpen, setIsPatientFormOpen] = useState(false);

  const fetchNotifications = async (moduleId?: number) => {
    try {
      const userId = auth.user?.authUserId;
      if (!userId) return;

      const response = await getNotificationEventsApi(userId, 0, 20, moduleId);

      if (response.success && response.data?.notificationEvents) {
        setNotifications(response.data.notificationEvents);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const userId = auth.user?.authUserId;
      if (userId == null) return;

      const response = await getNotificationUnreadCountApi(userId);

      if (response.success && typeof response.data === 'number') {
        setNotificationCount(response.data);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [auth.user?.authUserId]);

  // Check if we're on the settings page
  const isOnSettingsPage = location.pathname === '/dashboard/settings';

  const handleLogout = async () => {
    const platform = getPlatformInfo();
    const deviceId = getDeviceId();
    await toast.promise(
      logoutMutation.mutateAsync({
        refreshToken: auth.refreshToken || "",
        platform: platform.browser || "",
        deviceId: deviceId,
        authUserId: auth.user?.authUserId || 0,
        userId: auth.user?.authUserId || 0,
      }), {
        pending: "Logging out...",
        success: "Logged out successfully"
      },
      {
        autoClose: 1500,
      }
    );
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return 'Good Morning';
    } else if (currentHour < 16) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  const handleQRCode = () => {
    if (!practiceInfo?.guid) {
      console.error('Practice GUID not available');
      return;
    }
    setIsQRCodeModalOpen(true);
  };

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);


  return (
    <>
    <header className={`w-full bg-white shadow-sm ${className}`}>
      <div className="w-full">
        {/* Top Bar */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
          <div className="flex items-center">
            <button
              onClick={() => toggleSidebar()}
              className="h-[20px] mr-1.5 bg-transparent rounded-sm-plus opacity-80 items-center justify-center hover:rotate-180 transition-colors flex md:hidden"
            >
              <img src="/images/img_text_indent_left.svg" alt="menu" className='rotate-180'/>
            </button>
            <span className="text-sm sm:text-base font-medium text-ink-muted">
              {getGreeting()}, {fullName}
            </span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 md:gap-6">
            {/* Dashboard Settings Button with Text - Visible on all pages */}
            <button
              onClick={() => checkAndNavigate(() => navigate('/dashboard/settings'))}
              className="cursor-pointer hover:opacity-70 transition-opacity flex items-center gap-1 sm:gap-2"
              title="Dashboard Settings"
            >
              <i className="bi bi-gear flex w-4 h-4 sm:w-4 sm:h-4"></i>
              {!isOnSettingsPage && (
                <span className="hidden lg:inline whitespace-nowrap text-sm font-normal text-gray-600">
                  Customize Dashboard
                </span>
              )}
            </button>

            {/* QR Code Button */}
            <button
              onClick={handleQRCode}
              className="cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center"
              title="Patient check-in"
            >
              <i className="bi bi-qr-code flex w-4 h-4 sm:w-4 sm:h-4"></i>
            </button>

            {/* Link New Patient Button */}
            <button
              onClick={() => setIsPatientFormOpen(true)}
              className="cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center"
              title="Link New Patient"
            >
              <i className="bi bi-person-add flex w-4 h-4 sm:w-4 sm:h-4"></i>
            </button>

            {/* Communication Button - Hidden on very small screens */}
            <button
              onClick={() => checkAndNavigate(() => navigate('/communication/messages'))}
              className="hidden min-[400px]:flex cursor-pointer hover:opacity-70 transition-opacity items-center justify-center"
              title="Communication"
            >
              <i className="bi bi-chat-dots flex w-4 h-4 sm:w-4 sm:h-4"></i>
            </button>

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => {
                  const willOpen = !showNotifications;
                  setShowNotifications(willOpen);
                  if (willOpen) {
                    fetchNotifications();
                    fetchUnreadCount();
                  }
                }}
                className="cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center relative"
              >
                <i className="bi bi-bell flex w-4 h-4 sm:w-4 sm:h-4"></i>
                {notificationCount > 0 && (
                  <span className="absolute -top-[9px] -right-[6px] bg-red-500 text-white text-[10px] font-semibold rounded-full min-w-[16px] h-4 px-1.5 flex items-center justify-center">
                    {notificationCount}
                  </span>
                )}
              </button>
              <NotificationPanel 
                isOpen={showNotifications}
                onClose={() => setShowNotifications(false)}
                notifications={notifications}
                onFilterChange={(moduleId) => fetchNotifications(moduleId)}
                onNotificationClick={() => {
                  fetchNotifications();
                  fetchUnreadCount();
                }}
              />
            </div>

            {/* Logout */}
            {logoutMutation.isPending ? (
              <i className="bi bi-arrow-clockwise flex w-4 h-4 sm:w-4 sm:h-4 animate-spin"></i>
            ) : (
              <button
                onClick={handleLogout}
                className="cursor-pointer hover:text-red-600 flex items-center justify-center"
              >
                <i className="bi bi-power flex w-4 h-4 sm:w-4 sm:h-4"></i>
              </button>
            )}
            
          </div>
        </div>
        
      </div>
    </header>
    {title && (
      <div className='text-xl font-medium text-ink py-2 px-4'>
        {title}
      </div>
    )}
    {/* QR Code Modal for Medical Records */}
    <QRCodeModal
      isOpen={isQRCodeModalOpen}
      onClose={() => setIsQRCodeModalOpen(false)}
      guid={practiceInfo?.guid}
    />

    {/* Link New Patient Form Modal */}
    <LinkNewPatientForm
      isOpen={isPatientFormOpen}
      onClose={() => setIsPatientFormOpen(false)}
      onSuccess={(patientData) => {
        console.log('Patient invitation sent successfully:', patientData);
      }}
    />
    </>
  );
};

export default Header;